<template>
  <!-- 实际水印挂载到 document.body；登录页不显示 -->
  <span class="global-watermark-anchor" aria-hidden="true"></span>
</template>

<script>
const WATERMARK_TEXT = '石家庄齐凌科技有限公司'
const MARKER = 'ql-wm'
const ROTATE = -22

function createTileDataUrl(text, options) {
  const {
    width = 480,
    height = 320,
    fontSize = 16,
    rotate = ROTATE,
    color = 'rgba(120, 120, 120, 0.12)',
    fontFamily = 'Microsoft YaHei, PingFang SC, sans-serif'
  } = options

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  ctx.clearRect(0, 0, width, height)
  ctx.save()
  ctx.translate(width / 2, height / 2)
  ctx.rotate((Math.PI / 180) * rotate)
  ctx.font = `500 ${fontSize}px ${fontFamily}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillStyle = color
  ctx.fillText(text, 0, 0)
  ctx.restore()

  return canvas.toDataURL('image/png')
}

function lockedStyle(bgImage, zIndex) {
  return [
    'position:fixed',
    'inset:0',
    'left:0',
    'top:0',
    'right:0',
    'bottom:0',
    'width:100% !important',
    'height:100% !important',
    'margin:0 !important',
    'padding:0 !important',
    'pointer-events:none !important',
    'user-select:none !important',
    '-webkit-user-select:none !important',
    'overflow:hidden !important',
    `z-index:${zIndex} !important`,
    'opacity:1 !important',
    'visibility:visible !important',
    'display:block !important',
    'background-repeat:repeat !important',
    'background-position:0 0 !important',
    `background-image:url("${bgImage}") !important`,
    'background-color:transparent !important',
    'transform:none !important',
    'clip:auto !important',
    'clip-path:none !important',
    'filter:none !important',
    '-webkit-print-color-adjust:exact !important',
    'print-color-adjust:exact !important'
  ].join(';')
}

export default {
  name: 'GlobalWatermark',
  data() {
    return {
      layerIds: [],
      observer: null,
      timer: null,
      rebuilding: false,
      bgImage: ''
    }
  },
  mounted() {
    this.applyTheme()
    this.mountLayers()
    this.bindObserver()
    this.timer = setInterval(() => this.ensureIntact(), 1000)
    window.addEventListener('resize', this.onResize)
    document.addEventListener('visibilitychange', this.onVisibility)
  },
  watch: {
    '$route.fullPath'() {
      this.applyTheme()
      this.rebuild()
    }
  },
  beforeDestroy() {
    this.teardown(true)
  },
  methods: {
    isLoginPage() {
      const path = (this.$route && this.$route.path) || ''
      const name = (this.$route && this.$route.name) || ''
      return path === '/login' || path === '/' || name === 'Login'
    },
    isDarkScreen() {
      const path = (this.$route && this.$route.path) || ''
      return path === '/data-dashboard' || path.includes('data-dashboard')
    },
    applyTheme() {
      // 单层、统一倾斜方向
      this.bgImage = createTileDataUrl(WATERMARK_TEXT, {
        width: 240,
        height: 150,
        fontSize: this.isDarkScreen() ? 16 : 15,
        rotate: ROTATE,
        color: this.isDarkScreen()
          ? 'rgba(190, 225, 255, 0.18)'
          : 'rgba(100, 100, 100, 0.12)'
      })
    },
    onResize() {
      this.rebuild()
    },
    onVisibility() {
      if (document.visibilityState === 'visible') this.ensureIntact()
    },
    genId() {
      return `${MARKER}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
    },
    createLayer(bg, zIndex) {
      const el = document.createElement('div')
      const id = this.genId()
      el.id = id
      el.setAttribute(`data-${MARKER}`, '1')
      el.setAttribute('aria-hidden', 'true')
      el.style.cssText = lockedStyle(bg, zIndex)
      document.body.appendChild(el)
      return id
    },
    mountLayers() {
      this.removeLayers()
      if (this.isLoginPage()) return
      this.layerIds = [this.createLayer(this.bgImage, 2147483646)]
    },
    removeLayers() {
      this.layerIds.forEach(id => {
        const el = document.getElementById(id)
        if (el && el.parentNode) el.parentNode.removeChild(el)
      })
      document.querySelectorAll(`[data-${MARKER}="1"]`).forEach(node => {
        if (node.parentNode) node.parentNode.removeChild(node)
      })
      this.layerIds = []
    },
    isLayerValid(id) {
      const el = document.getElementById(id)
      if (!el || !document.body.contains(el)) return false
      if (el.getAttribute(`data-${MARKER}`) !== '1') return false
      const style = window.getComputedStyle(el)
      if (style.display === 'none' || style.visibility === 'hidden') return false
      if (Number(style.opacity) < 0.6) return false
      if (style.pointerEvents !== 'none') return false
      if (!style.backgroundImage || style.backgroundImage === 'none') return false
      if (!/data:image/i.test(style.backgroundImage)) return false
      const rect = el.getBoundingClientRect()
      if (rect.width < window.innerWidth * 0.85 || rect.height < window.innerHeight * 0.85) return false
      return true
    },
    ensureIntact() {
      if (this.rebuilding) return
      if (this.isLoginPage()) {
        if (this.layerIds.length) this.removeLayers()
        return
      }
      const ok = this.layerIds.length === 1 && this.isLayerValid(this.layerIds[0])
      if (!ok) this.rebuild()
    },
    rebuild() {
      if (this.rebuilding) return
      this.rebuilding = true
      try {
        if (this.observer) this.observer.disconnect()
        this.applyTheme()
        this.mountLayers()
        this.bindObserver()
      } finally {
        this.$nextTick(() => {
          this.rebuilding = false
        })
      }
    },
    bindObserver() {
      if (this.observer) this.observer.disconnect()
      if (this.isLoginPage()) return
      this.observer = new MutationObserver(() => {
        if (!this.rebuilding) this.ensureIntact()
      })
      this.observer.observe(document.body, {
        childList: true,
        subtree: false
      })
      this.layerIds.forEach(id => {
        const el = document.getElementById(id)
        if (el) {
          this.observer.observe(el, {
            attributes: true,
            attributeFilter: ['style', 'class', 'id', `data-${MARKER}`]
          })
        }
      })
    },
    teardown(removeDom) {
      window.removeEventListener('resize', this.onResize)
      document.removeEventListener('visibilitychange', this.onVisibility)
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
      if (removeDom) this.removeLayers()
    }
  }
}
</script>
